/**
 * SpojOy Design System
 * 모던, 미니멀, 모노톤 디자인 시스템
 */

// 색상 팔레트
export const colors = {
  // 기본 색상
  black: '#000',
  white: '#fff',

  // 그레이스케일
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eee',
    300: '#ddd',
    400: '#bbb',
    500: '#999',
    600: '#666',
    700: '#444',
    800: '#333',
    900: '#111',
  },

  // 상태 색상 (텍스트 전용)
  status: {
    success: '#2e7d32',
    error: '#c62828',
    warning: '#f57c00',
    info: '#1565c0',
  },
};

// 타이포그래피
export const typography = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  fontFamilyMono: "'SF Mono', 'Monaco', 'Consolas', monospace",

  fontSize: {
    xs: '11px',
    sm: '12px',
    base: '13px',
    md: '14px',
    lg: '15px',
    xl: '17px',
    '2xl': '22px',
  },

  fontWeight: {
    normal: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
  },

  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },
};

// 간격
export const spacing = {
  0: '0',
  1: '4px',
  2: '6px',
  3: '8px',
  4: '10px',
  5: '12px',
  6: '14px',
  7: '16px',
  8: '20px',
  9: '24px',
  10: '32px',
};

// 공통 스타일
export const commonStyles = {
  // 컨테이너
  container: {
    backgroundColor: colors.white,
    padding: `0 ${spacing[7]}`,
  },

  // 섹션
  section: {
    backgroundColor: colors.gray[50],
    padding: spacing[5],
    border: `1px solid ${colors.gray[200]}`,
  },

  // 제목
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[7],
  },

  sectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[5],
  },

  // 탭 컨테이너
  tabContainer: {
    display: 'flex',
    borderBottom: `1px solid ${colors.gray[300]}`,
    marginBottom: spacing[7],
    backgroundColor: colors.white,
  },

  // 탭 버튼 (기본) - 비활성 탭은 컨테이너 바닥선 표시
  tabButton: {
    padding: `${spacing[4]} ${spacing[7]}`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    border: 'none',
    borderBottom: `2px solid transparent`,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: colors.gray[600],
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
    marginBottom: '-1px',
    position: 'relative' as const,
    outline: 'none',
  },

  // 탭 버튼 (활성) - 활성 탭만 바닥선 표시
  // fontWeight 변경 제거: 굵기 변경 시 텍스트 너비가 달라져 탭 위치 흔들림 발생
  tabButtonActive: {
    color: colors.black,
    borderBottom: `2px solid ${colors.black}`,
  },

  // 폼 그룹 (인라인)
  formGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },

  // 폼 그룹 (2행 스타일 - 라벨 위, 입력 아래)
  formGroupVertical: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[2],
  },

  // 라벨
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    minWidth: '70px',
    color: colors.gray[800],
    whiteSpace: 'nowrap' as const,
  },

  // 입력 필드
  input: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    outline: 'none',
    whiteSpace: 'nowrap' as const,
  },

  // 셀렉트
  select: {
    padding: `${spacing[2]} ${spacing[4]}`,
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    minWidth: '120px',
    outline: 'none',
    whiteSpace: 'nowrap' as const,
  },

  // 텍스트에어리어
  textarea: {
    width: '100%',
    padding: spacing[4],
    fontSize: typography.fontSize.base,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    fontFamily: typography.fontFamilyMono,
    resize: 'vertical' as const,
    outline: 'none',
  },

  // 버튼 (기본)
  button: {
    padding: `${spacing[2]} ${spacing[6]}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    border: `1px solid ${colors.black}`,
    borderRadius: '0',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
    outline: 'none',
  },

  // 버튼 (primary)
  buttonPrimary: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },

  // 버튼 (secondary)
  buttonSecondary: {
    backgroundColor: colors.white,
    color: colors.black,
    border: `1px solid ${colors.black}`,
  },

  // 버튼 (danger)
  buttonDanger: {
    backgroundColor: 'transparent',
    color: colors.gray[600],
    border: `1px solid ${colors.gray[500]}`,
  },

  // 테이블 컨테이너
  tableContainer: {
    overflowX: 'auto' as const,
    overflowY: 'auto' as const,
    border: `1px solid ${colors.gray[200]}`,
  },

  // 테이블
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
  },

  // 테이블 헤더
  th: {
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[300]}`,
    textAlign: 'left' as const,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[800],
    whiteSpace: 'nowrap' as const,
  },

  // 테이블 셀
  td: {
    padding: `${spacing[3]} ${spacing[4]}`,
    borderBottom: `1px solid ${colors.gray[200]}`,
    color: colors.gray[700],
    whiteSpace: 'nowrap' as const,
  },

  // 상태 텍스트 (배지 대체)
  statusText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    whiteSpace: 'nowrap' as const,
  },

  statusSuccess: {
    color: colors.status.success,
  },

  statusError: {
    color: colors.status.error,
  },

  statusWarning: {
    color: colors.status.warning,
  },

  statusInfo: {
    color: colors.status.info,
  },

  statusDefault: {
    color: colors.gray[500],
  },

  // 통계 아이템
  statItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: `${spacing[4]} ${spacing[6]}`,
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[200]}`,
    minWidth: '100px',
  },

  statLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.gray[500],
    marginBottom: spacing[1],
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
  },

  statValue: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    whiteSpace: 'nowrap' as const,
  },

  // 페이지네이션
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing[7],
    padding: `${spacing[5]} 0`,
  },

  paginationButton: {
    padding: `${spacing[2]} ${spacing[5]}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    color: colors.gray[800],
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '70px',
    whiteSpace: 'nowrap' as const,
    outline: 'none',
  },

  paginationButtonDisabled: {
    backgroundColor: colors.gray[100],
    color: colors.gray[400],
    cursor: 'not-allowed',
  },

  paginationInfo: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
    fontWeight: typography.fontWeight.medium,
    whiteSpace: 'nowrap' as const,
  },

  // 로딩/빈 상태
  emptyState: {
    textAlign: 'center' as const,
    padding: spacing[10],
    color: colors.gray[500],
    fontSize: typography.fontSize.base,
  },

  loadingState: {
    textAlign: 'center' as const,
    padding: spacing[10],
    color: colors.gray[600],
    fontSize: typography.fontSize.base,
  },

  // 힌트 텍스트
  hint: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
  },

  // 체크박스 그룹
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[1],
  },

  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
  },

  // iframe
  iframe: {
    width: '100%',
    height: '75vh',
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
  },
};

// 스타일 헬퍼 함수
export const getStatusStyle = (status: 'success' | 'error' | 'warning' | 'info' | 'default') => ({
  ...commonStyles.statusText,
  ...(status === 'success' ? commonStyles.statusSuccess :
     status === 'error' ? commonStyles.statusError :
     status === 'warning' ? commonStyles.statusWarning :
     status === 'info' ? commonStyles.statusInfo :
     commonStyles.statusDefault),
});

export const getTabButtonStyle = (isActive: boolean) => ({
  ...commonStyles.tabButton,
  ...(isActive ? commonStyles.tabButtonActive : {}),
});

export const getButtonStyle = (variant: 'primary' | 'secondary' | 'danger' = 'primary') => ({
  ...commonStyles.button,
  ...(variant === 'primary' ? commonStyles.buttonPrimary :
     variant === 'secondary' ? commonStyles.buttonSecondary :
     commonStyles.buttonDanger),
});

export default commonStyles;
